import { useState, useEffect, Fragment } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Text, Spinner, Badge, Field, Input, Textarea,
  makeStyles, tokens,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
  MessageBar, MessageBarBody,
  Divider,
  Avatar,
} from '@fluentui/react-components'
import { ArrowLeft24Regular, Edit24Regular, CalendarEdit24Regular, InfoRegular, BuildingBank24Regular, ClipboardTask24Regular, ChevronDown16Filled, ChevronRight16Filled } from '@fluentui/react-icons'
import { documentDefinitionsService, DocTypeLabels, type DocType, type EmployeeDocumentRecord } from '../../services/documentDefinitionsService'
import { employeesService, EMPLOYEE_STATUS } from '../../services/employeesService'
import { vacationBalanceService } from '../../services/vacationBalanceService'
import VacationTiles from '../../components/VacationTiles'
import { pensionContributionsService, FrequencyLabels, ContributionTypeLabels } from '../../services/pensionContributionsService'
import { useAuth } from '../../auth/useAuth'
import { getEntraUserPhoto } from '../../services/graphService'
import { NativeDateInput } from '../../components/NativeDateInput'

const IS_MOCK = import.meta.env.VITE_MOCK_AUTH === 'true'

const useStyles = makeStyles({
  header:      { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' },
  card:        { backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusMedium, padding: '24px', boxShadow: tokens.shadow4 },
  // Info grid — Varianta A
  infoGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 32px', marginTop: '16px' },
  infoGrid3:   { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px 32px', marginTop: '16px' },
  infoLabel:   {
    fontSize: '12px', fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '.07em', color: tokens.colorNeutralForeground3, marginBottom: '2px',
  },
  infoValue:   { fontSize: '14px', fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, lineHeight: '1.4' },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1,
    paddingBottom: '10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  subsection:  {
    fontSize: '12px', fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: '.06em', color: tokens.colorNeutralForeground2,
    marginTop: '20px', paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralBackground3}`,
    marginBottom: '0',
  },
  // Dovolená
  balRow:   { display: 'flex', gap: '24px', flexWrap: 'wrap', margin: '16px 0' },
  stat:     { display: 'flex', flexDirection: 'column', minWidth: '80px' },
  statVal:  { fontSize: '28px', fontWeight: 700, lineHeight: 1.1 },
  statLbl:  { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  resetNote:{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '8px' },
  formGrid: { display: 'flex', flexDirection: 'column', gap: '12px' },
  autoHint: { fontSize: '12px', color: tokens.colorNeutralForeground3, display: 'flex', alignItems: 'center', gap: '4px' },
})

const EMPLOYMENT_LABELS: Record<number, string> = {
  1: 'HPP – Hlavní pracovní poměr',
  2: 'DPP – Dohoda o provedení práce',
  3: 'DPC – Dohoda o pracovní činnosti',
}

const CONTRACT_LABELS: Record<number, string> = {
  1: 'Na dobu neurčitou',
  2: 'Na dobu určitou',
  3: 'DPP',
  4: 'DPČ',
}

const DAY_LABELS: Record<string, string> = {
  mon: 'Po', tue: 'Út', wed: 'St', thu: 'Čt', fri: 'Pá',
}

const fmt = (date: string | null | undefined) =>
  date ? new Date(date).toLocaleDateString('cs-CZ') : null

// ── Pomocná komponenta — jedno pole (skryje se pokud je hodnota prázdná) ─────
function InfoField({ label, value, children, span }: {
  label: string
  value?: string | null
  children?: React.ReactNode
  span?: boolean
}) {
  const styles = useStyles()
  const content = children ?? value
  if (content === null || content === undefined || content === '') return null
  return (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <div className={styles.infoLabel}>{label}</div>
      <div className={styles.infoValue}>{content}</div>
    </div>
  )
}

// ── Karta zůstatku dovolené ───────────────────────────────────────────────
function VacationCard({ employeeId, employmentType, styles, canEdit }: {
  employeeId: number
  employmentType: number
  styles: ReturnType<typeof useStyles>
  canEdit: boolean
}) {
  const queryClient = useQueryClient()
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formError, setFormError] = useState('')
  const [form, setForm] = useState({ entitlement: '', carriedOver: '', validFrom: '', note: '' })
  const [calcRows, setCalcRows] = useState<{ from: string; to: string; wh: string }[]>([])
  const [showCalc, setShowCalc] = useState(false)

  const { data: balance, isLoading } = useQuery({
    queryKey: ['vacation-balance', employeeId, year],
    queryFn: () => vacationBalanceService.getBalance(employeeId, year),
    staleTime: 0,                 // #26: po opravě žádosti/úvazku ukázat aktuální zůstatek bez ručního obnovení
    refetchOnMount: 'always',
  })

  // #23 Fáze 3: historie úvazku pro předvyplnění kalkulačky nároku
  const { data: workloadHistory = [] } = useQuery({
    queryKey: ['workload-history', employeeId],
    queryFn: () => employeesService.getWorkloadHistory(employeeId),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  // Předvyplnění řádků kalkulačky z evidovaných období úvazku oříznutých do zvoleného roku.
  const weeklyOfJson = (json: string | null, fallback: number) => {
    if (!json) return fallback
    try { const s = JSON.parse(json); return ['mon', 'tue', 'wed', 'thu', 'fri'].reduce((a, d) => a + (s[d] ?? 0), 0) } catch { return fallback }
  }
  const addDaysIso = (iso: string, days: number) => {
    const d = new Date(iso); d.setDate(d.getDate() + days); return d.toISOString().split('T')[0]
  }
  const buildCalcRowsFromHistory = (): { from: string; to: string; wh: string }[] => {
    const yStart = balance?.validFrom?.split('T')[0] || `${year}-01-01`
    const yEnd = `${year}-12-31`
    const periods = (workloadHistory ?? [])
      .map(p => ({ from: p.validFrom.split('T')[0], weekly: weeklyOfJson(p.workScheduleJson, p.weeklyHours) }))
      .sort((a, b) => a.from.localeCompare(b.from))
    const rows: { from: string; to: string; wh: string }[] = []
    for (let i = 0; i < periods.length; i++) {
      const p = periods[i]
      const next = periods[i + 1]
      const segTo = next ? addDaysIso(next.from, -1) : yEnd   // období končí den před dalším; poslední do konce roku
      const from = p.from > yStart ? p.from : yStart
      const to = segTo < yEnd ? segTo : yEnd
      if (from > yEnd || to < yStart || to < from) continue   // mimo zvolený rok
      rows.push({ from, to, wh: (p.weekly % 1 === 0 ? String(p.weekly) : p.weekly.toFixed(1)).replace('.', ',') })
    }
    return rows.length ? rows : [{ from: yStart, to: yEnd, wh: '' }]
  }

  const parseDecimal = (s: string) => Number(s.replace(',', '.'))

  const resetMutation = useMutation({
    mutationFn: () => vacationBalanceService.reset(employeeId, {
      year,
      entitlement: parseDecimal(form.entitlement),
      carriedOver: parseDecimal(form.carriedOver),
      validFrom:   form.validFrom || undefined,
      note:        form.note || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vacation-balance', employeeId] })
      setDialogOpen(false)
      setFormError('')
    },
    onError: () => setFormError('Chyba při ukládání. Zkontrolujte hodnoty.'),
  })

  const openDialog = () => {
    if (balance) {
      setForm({
        entitlement: String(balance.entitlementHours).replace('.', ','),
        carriedOver: String(balance.carriedOverHours).replace('.', ','),
        validFrom:   balance.validFrom?.split('T')[0] ?? `${year}-01-01`,
        note:        '',
      })
    } else {
      setForm({ entitlement: '', carriedOver: '0', validFrom: `${year}-01-01`, note: '' })
    }
    setCalcRows(buildCalcRowsFromHistory())   // #23 Fáze 3: předvyplnit z historie úvazku
    setShowCalc(balance?.entitlementReviewNeeded ?? false)
    setFormError('')
    setDialogOpen(true)
  }

  // #23 Fáze 3: při přepnutí roku (s otevřenou kalkulačkou) znovu předvyplnit z historie pro daný rok
  useEffect(() => {
    if (dialogOpen && showCalc) setCalcRows(buildCalcRowsFromHistory())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year])

  const handleSubmit = () => {
    setFormError('')
    const ent = parseDecimal(form.entitlement)
    const co  = parseDecimal(form.carriedOver)
    if (isNaN(ent) || ent < 0) { setFormError('Nárok musí být kladné číslo.'); return }
    if (isNaN(co)  || co  < 0) { setFormError('Převedené hodiny musí být kladné číslo.'); return }
    resetMutation.mutate()
  }

  // Pomocník: převod zadaných hodin na dny (pro info vedle pole)
  const avgH = balance?.averageDailyHours ?? 8
  const toDays = (raw: string) => {
    const h = parseDecimal(raw)
    if (isNaN(h) || h < 0 || avgH <= 0) return null
    const d = h / avgH
    return d % 1 === 0 ? String(d) : d.toFixed(1).replace('.', ',')
  }

  const years = [currentYear - 1, currentYear, currentYear + 1]

  // #20/#23 Kalkulačka nároku při změně úvazku — N období (od–do + týdenní úvazek v h)
  // Český výpočet (ZP §211–223): (celé odpracované týdny ÷ 52) × výměra × týdenní doba, nahoru na celé hodiny.
  // Celé týdny se počítají souvisle přes navazující období (rok = 52 celých týdnů); viz weeksByIndex níže.
  // Náhradní doby i svátky se počítají jako odpracované.
  const yearStart = balance?.validFrom?.split('T')[0] || `${year}-01-01`
  const yearEnd = `${year}-12-31`
  const nextDay = (iso: string) => {
    if (!iso) return yearStart
    const d = new Date(iso); d.setDate(d.getDate() + 1)
    return d.toISOString().split('T')[0]
  }
  const dayCountIso = (from: string, to: string) => {
    if (!from || !to) return 0
    const d = Math.round((new Date(to).getTime() - new Date(from).getTime()) / 86400000) + 1
    return d > 0 ? d : 0
  }
  const updateCalc = (i: number, k: 'from' | 'to' | 'wh', v: string) =>
    setCalcRows(rows => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r))
  // Nové období navazuje dnem po konci předchozího, konec iniciačně 31. 12.
  const addCalc = () => setCalcRows(rows => {
    const last = rows[rows.length - 1]
    return [...rows, { from: last ? nextDay(last.to) : yearStart, to: yearEnd, wh: '' }]
  })
  const removeCalc = (i: number) => setCalcRows(rows => rows.filter((_, idx) => idx !== i))

  // #23 Počet celých týdnů na období (index řádku → týdny). Rok má 52 celých týdnů; hraniční týden mezi
  // navazujícími obdobími se nesmí ztratit (samostatný floor po obdobích podhodnocuje). Navazující období
  // se počítají jako jeden souvislý úsek, 7denní blok připadne období s většinou dní. Mezera přeruší souvislost.
  const weeksByIndex: Record<number, number> = {}
  {
    const rows = calcRows
      .map((r, i) => ({ i, from: r.from, to: r.to, days: dayCountIso(r.from, r.to) }))
      .filter(r => r.days > 0)
      .sort((a, b) => a.from.localeCompare(b.from))
    for (const r of rows) weeksByIndex[r.i] = 0
    let k = 0
    while (k < rows.length) {
      let j = k
      const run = [rows[k]]
      while (j + 1 < rows.length && rows[j + 1].from === nextDay(rows[j].to)) { run.push(rows[j + 1]); j++ }
      let off = 0
      const segs = run.map(r => { const s = { i: r.i, start: off, end: off + r.days }; off += r.days; return s })
      const numWeeks = Math.floor(off / 7)
      for (let w = 0; w < numWeeks; w++) {
        const ws = w * 7, we = w * 7 + 7
        let bestI = segs[0].i, bestOv = -1
        for (const s of segs) {
          const ov = Math.min(we, s.end) - Math.max(ws, s.start)
          if (ov > bestOv) { bestOv = ov; bestI = s.i }
        }
        weeksByIndex[bestI] = (weeksByIndex[bestI] ?? 0) + 1
      }
      k = j + 1
    }
  }

  const VACATION_WEEKS = 5   // výměra: 25 dní = 5 týdnů
  let calcSum = 0
  for (let i = 0; i < calcRows.length; i++) {
    const wh = parseDecimal(calcRows[i].wh)
    if (isNaN(wh) || wh <= 0) continue
    calcSum += (weeksByIndex[i] ?? 0) * wh
  }
  const calcHours = calcSum > 0 ? Math.ceil(VACATION_WEEKS / 52 * calcSum) : 0

  return (
    <div className={styles.card} style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text size={500} weight="semibold">Dovolená</Text>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {years.map(y => (
              <Button key={y} size="small" appearance={y === year ? 'primary' : 'subtle'} onClick={() => setYear(y)}>{y}</Button>
            ))}
          </div>
          {canEdit && (
            <Button icon={<CalendarEdit24Regular />} onClick={openDialog} size="small">Korekce</Button>
          )}
        </div>
      </div>

      {isLoading && <Spinner size="tiny" style={{ marginTop: 12 }} />}

      {balance && (
        <>
          <div style={{ marginTop: 16 }}>
            <VacationTiles balance={balance} />
          </div>

          {canEdit && balance.entitlementReviewNeeded && (
            <MessageBar intent="warning" style={{ marginTop: 10 }}>
              <MessageBarBody>
                <strong>Úvazek se změnil</strong> — nárok pro {year} je potřeba přepočítat a uložit přes „Korekce".
              </MessageBarBody>
            </MessageBar>
          )}

          {canEdit && balance.workloadMismatchCount > 0 && (
            <MessageBar intent="warning" style={{ marginTop: 10 }}>
              <MessageBarBody>
                <strong>Dovolená přes změnu úvazku</strong> — {balance.workloadMismatchCount === 1
                  ? '1 žádost o dovolenou má'
                  : `${balance.workloadMismatchCount} žádostí o dovolenou má`} počet hodin, který nemusí odpovídat úvazku platnému v jejím termínu. Zkontrolujte je ve „Všech žádostech" (označené ⚠) a případně ručně opravte „Čerpané hodiny".
              </MessageBarBody>
            </MessageBar>
          )}

          {(employmentType === 2 || employmentType === 3) && (
            <div className={styles.autoHint} style={{ marginTop: 8 }}>
              <InfoRegular fontSize={14} />
              Uvedený počet dnů a hodin dovolené je pro DPP a DPČ pouze orientační.
            </div>
          )}

          {canEdit && balance.entitlement !== balance.autoEntitlement && (
            <div className={styles.autoHint} style={{ marginTop: 8 }}>
              <InfoRegular fontSize={14} />
              Auto-výpočet dle data nástupu: {balance.autoEntitlement % 1 === 0 ? balance.autoEntitlement : balance.autoEntitlement.toFixed(1).replace('.', ',')} dní (ručně upraveno)
            </div>
          )}

          {canEdit && balance.resetNote && (
            <div className={styles.resetNote}>
              Poslední korekce HR: „{balance.resetNote}"
              {balance.resetAt && ` — ${new Date(balance.resetAt).toLocaleDateString('cs-CZ')}`}
            </div>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={(_, d) => { if (!d.open) setDialogOpen(false) }}>
        <DialogSurface style={{ width: 'min(720px, 92vw)', maxWidth: 'min(720px, 92vw)' }}>
          <DialogBody>
            <DialogTitle>Korekce dovolené — {year}</DialogTitle>
            <DialogContent style={{ overflowX: 'hidden', scrollbarGutter: 'stable' }}>
            <div className={styles.formGrid}>
              {formError && <MessageBar intent="error"><MessageBarBody>{formError}</MessageBarBody></MessageBar>}
              {balance && (
                <div className={styles.autoHint} style={{ marginBottom: 4 }}>
                  <InfoRegular fontSize={14} />
                  Auto-výpočet dle data nástupu a úvazku:&nbsp;
                  <strong>
                    {(() => {
                      const h = balance.autoEntitlement * avgH
                      return (h % 1 === 0 ? String(h) : h.toFixed(1).replace('.', ',')) + ' h'
                    })()}
                  </strong>
                  &nbsp;=&nbsp;
                  {balance.autoEntitlement % 1 === 0 ? balance.autoEntitlement : balance.autoEntitlement.toFixed(1).replace('.', ',')} dní
                </div>
              )}
              <div style={{ border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 6, padding: 10 }}>
                <Button appearance="subtle" size="small"
                  icon={showCalc ? <ChevronDown16Filled /> : <ChevronRight16Filled />}
                  onClick={() => {
                  const open = !showCalc
                  setShowCalc(open)
                  if (open) setCalcRows(buildCalcRowsFromHistory())   // #23 Fáze 3: předvyplnit při otevření
                }}>
                  {showCalc ? 'Zavřít' : 'Otevřít'} kalkulačku nároku (změna úvazku)
                </Button>
                {showCalc && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 84px auto 26px', gap: '6px 8px', alignItems: 'center' }}>
                      <div style={{ fontSize: 11, color: tokens.colorNeutralForeground3 }}>Od</div>
                      <div style={{ fontSize: 11, color: tokens.colorNeutralForeground3 }}>Do</div>
                      <div style={{ fontSize: 11, color: tokens.colorNeutralForeground3, textAlign: 'center' }}>h/týden</div>
                      <div></div>
                      <div></div>
                      {calcRows.map((r, i) => (
                        <Fragment key={i}>
                          <NativeDateInput value={r.from} onChange={v => updateCalc(i, 'from', v)} />
                          <NativeDateInput value={r.to} onChange={v => updateCalc(i, 'to', v)} />
                          <Input type="text" inputMode="decimal" placeholder="h/týd"
                            value={r.wh} style={{ textAlign: 'center' }}
                            onChange={e => updateCalc(i, 'wh', (e.target?.value ?? '').replace(/[^0-9.,]/g, ''))} />
                          <span style={{ fontSize: 12, color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' }}>= {weeksByIndex[i] ?? 0} týd.</span>
                          {calcRows.length > 1
                            ? <Button appearance="subtle" size="small" onClick={() => removeCalc(i)}>×</Button>
                            : <span />}
                        </Fragment>
                      ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
                      <Button size="small" onClick={addCalc}>+ Přidat období</Button>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <strong style={{ fontSize: 16 }}>{calcHours} h</strong>
                        <Button size="small" appearance="primary" disabled={calcHours <= 0}
                          onClick={() => setForm(f => ({ ...f, entitlement: String(calcHours) }))}>
                          Použít do nároku
                        </Button>
                      </div>
                    </div>
                    <div className={styles.autoHint} style={{ marginTop: 8 }}>
                      <InfoRegular fontSize={14} />
                      Období jsou předvyplněna z evidované historie úvazku pro rok {year} — zkontrolujte, upravte a použijte.
                    </div>
                    <div className={styles.autoHint} style={{ marginTop: 4 }}>
                      <InfoRegular fontSize={14} />
                      ⌈ 5 týdnů ÷ 52 × Σ(celé týdny období × h/týden) ⌉ — zaokrouhleno nahoru na celé hodiny (ZP § 211–223).
                    </div>
                  </div>
                )}
              </div>
              <Field
                label="Nárok (h)"
                hint={toDays(form.entitlement) !== null ? `= ${toDays(form.entitlement)} dní · Základní přidělený počet hodin — lze přepsat auto-výpočet` : 'Základní přidělený počet hodin — lze přepsat auto-výpočet'}
                required
              >
                <Input type="text" inputMode="decimal" value={form.entitlement}
                  placeholder="např. 200 nebo 100,5"
                  onChange={e => {
                    const raw = (e.target?.value ?? '').replace(/[^0-9.,]/g, '')
                    setForm(f => ({ ...f, entitlement: raw }))
                  }} />
              </Field>
              <Field
                label="Převedeno z minulého roku (h)"
                hint={toDays(form.carriedOver) !== null ? `= ${toDays(form.carriedOver)} dní` : undefined}
              >
                <Input type="text" inputMode="decimal" value={form.carriedOver}
                  placeholder="např. 0 nebo 16"
                  onChange={e => {
                    const raw = (e.target?.value ?? '').replace(/[^0-9.,]/g, '')
                    setForm(f => ({ ...f, carriedOver: raw }))
                  }} />
              </Field>
              <Field label="Platnost od" hint="Datum nástupu nebo 1. 1. daného roku">
                <NativeDateInput value={form.validFrom} onChange={v => setForm(f => ({ ...f, validFrom: v }))} />
              </Field>
              <Field label="Poznámka HR" hint="Důvod korekce (vidí jen HR)">
                <Textarea rows={2} value={form.note}
                  onChange={(_, d) => setForm(f => ({ ...f, note: d.value }))} />
              </Field>
              <MessageBar intent="warning">
                <MessageBarBody>Čerpané dny se přepočítají automaticky ze schválených žádostí.</MessageBarBody>
              </MessageBar>
            </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDialogOpen(false)}>Zrušit</Button>
              <Button appearance="primary" disabled={resetMutation.isPending} onClick={handleSubmit}>
                {resetMutation.isPending ? 'Ukládám...' : 'Uložit'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}

// ── Pracovní rozvrh (readonly) ────────────────────────────────────────────

function WorkScheduleCard({ workScheduleJson, weeklyHours, styles }: {
  workScheduleJson: string | null
  weeklyHours: number
  styles: ReturnType<typeof useStyles>
}) {
  const days = ['mon', 'tue', 'wed', 'thu', 'fri']
  let schedule: Record<string, number> | null = null
  if (workScheduleJson) {
    try { schedule = JSON.parse(workScheduleJson) } catch { /* ignore */ }
  }
  const weekly = schedule
    ? days.reduce((s, d) => s + (schedule![d] ?? 0), 0)
    : weeklyHours

  return (
    <div className={styles.card} style={{ marginTop: 24 }}>
      <Text size={500} weight="semibold">Pracovní rozvrh</Text>
      <Divider style={{ margin: '12px 0' }} />
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className={styles.stat}>
          <span className={styles.statVal}>{Math.round(weekly / 40 * 100)} %</span>
          <span className={styles.statLbl}>Úvazek</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statVal}>{weekly.toFixed(weekly % 1 === 0 ? 0 : 1)}</span>
          <span className={styles.statLbl}>Hod / týden</span>
        </div>
        {schedule && (
          <div style={{ display: 'flex', gap: 16 }}>
            {days.map(d => (
              <div key={d} className={styles.stat} style={{ minWidth: 36, textAlign: 'center' }}>
                <span style={{ fontWeight: 600 }}>{schedule![d] ?? 0} h</span>
                <span className={styles.statLbl}>{DAY_LABELS[d]}</span>
              </div>
            ))}
          </div>
        )}
        {!schedule && weekly === 40 && (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Standardní rozvrh: Po–Pá 8 h</Text>
        )}
      </div>
    </div>
  )
}

// ── Historie úvazku (#23 Fáze 2) ──────────────────────────────────────────
function WorkloadHistoryCard({ employeeId, styles }: {
  employeeId: number
  styles: ReturnType<typeof useStyles>
}) {
  const { data = [] } = useQuery({
    queryKey: ['workload-history', employeeId],
    queryFn: () => employeesService.getWorkloadHistory(employeeId),
    staleTime: 0,                 // #23: po první změně úvazku se sekce musí objevit bez F5
    refetchOnMount: 'always',
  })
  if (data.length <= 1) return null   // informační blok jen při více záznamech; editace je v režimu úpravy
  const fmtH = (h: number) => (h % 1 === 0 ? String(h) : h.toFixed(1).replace('.', ','))
  const weeklyOf = (json: string | null, fallback: number) => {
    if (!json) return fallback
    try { const s = JSON.parse(json); return ['mon', 'tue', 'wed', 'thu', 'fri'].reduce((a, d) => a + (s[d] ?? 0), 0) } catch { return fallback }
  }
  return (
    <div className={styles.card} style={{ marginTop: 24 }}>
      <Text size={500} weight="semibold">Historie úvazku</Text>
      <Divider style={{ margin: '12px 0' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.map((p, i) => {
          const weekly = weeklyOf(p.workScheduleJson, p.weeklyHours)
          return (
            <div key={p.id} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={{ minWidth: 92, fontWeight: 600 }}>od {new Date(p.validFrom).toLocaleDateString('cs-CZ')}</span>
              <span>{fmtH(weekly)} h/týden <span style={{ color: tokens.colorNeutralForeground3 }}>({Math.round(weekly / 40 * 100)} %)</span></span>
              {i === 0 && <span style={{ fontSize: 12, color: tokens.colorBrandForeground1 }}>· aktuální</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Karta příspěvku na stáří (readonly) ──────────────────────────────────
function PensionContributionCard({ employeeId, styles }: {
  employeeId: number
  styles: ReturnType<typeof useStyles>
}) {
  const { data } = useQuery({
    queryKey: ['pension-contributions', { employeeId, activeOnly: true }],
    queryFn: () => pensionContributionsService.getAll({ employeeId, activeOnly: true }),
  })

  const active = data?.[0] ?? null

  return (
    <div className={styles.card} style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <BuildingBank24Regular />
        <Text size={500} weight="semibold">Příspěvek na stáří</Text>
      </div>
      {active ? (
        <div className={styles.infoGrid}>
          <InfoField label="Penzijní ústav"    value={active.pensionProvider} />
          <InfoField label="Výše příspěvku">
            <Text weight="semibold">{active.amount.toLocaleString('cs-CZ')} Kč</Text>
          </InfoField>
          <InfoField label="Frekvence"         value={FrequencyLabels[active.frequency]} />
          <InfoField label="Typ"               value={ContributionTypeLabels[active.contributionType]} />
          {active.validFrom && (
            <InfoField label="Platnost od"     value={active.validFrom.slice(0, 10).split('-').reverse().join('.')} />
          )}
        </div>
      ) : (
        <Text style={{ color: tokens.colorNeutralForeground3 }}>Žádný aktivní příspěvek na stáří.</Text>
      )}
    </div>
  )
}

// ── Karta dokumentů ───────────────────────────────────────────────────────
function DocumentsCard({ employeeId, styles, canSign }: {
  employeeId: number
  styles: ReturnType<typeof useStyles>
  canSign: boolean
}) {
  const queryClient = useQueryClient()
  const { data = [] } = useQuery({
    queryKey: ['employee-documents', employeeId],
    queryFn: () => documentDefinitionsService.getEmployeeDocuments(employeeId),
  })

  const completeMutation = useMutation({
    mutationFn: (empDocId: number) => documentDefinitionsService.completeDocument(empDocId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['employee-documents', employeeId] }),
  })

  if (data.length === 0) return null

  const thStyle: React.CSSProperties = {
    textAlign: 'left', padding: '8px 12px',
    borderBottom: `2px solid ${tokens.colorNeutralStroke2}`,
    fontWeight: 600, fontSize: 12, color: tokens.colorNeutralForeground2,
    background: tokens.colorNeutralBackground2,
  }
  const tdStyle: React.CSSProperties = {
    padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: 13, verticalAlign: 'middle',
  }

  return (
    <div className={styles.card} style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <ClipboardTask24Regular />
        <Text size={500} weight="semibold">Dokumenty</Text>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle}>Název</th>
            <th style={thStyle}>Typ</th>
            <th style={thStyle}>Termín</th>
            <th style={thStyle}>Stav</th>
            <th style={thStyle}>Splněno</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {data.map((doc: EmployeeDocumentRecord) => {
            const overdue = !doc.isCompleted && doc.deadlineDate && new Date(doc.deadlineDate) < new Date()
            return (
              <tr key={doc.id}>
                <td style={tdStyle}>
                  {doc.documentUrl
                    ? <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1 }}>{doc.documentName}</a>
                    : doc.documentName}
                </td>
                <td style={tdStyle}>
                  <Badge appearance="outline" color={doc.documentType === 1 ? 'warning' : doc.documentType === 3 ? 'success' : 'informative'}>
                    {DocTypeLabels[doc.documentType as DocType]}
                  </Badge>
                </td>
                <td style={{ ...tdStyle, fontSize: 12 }}>
                  {doc.deadlineDate ? new Date(doc.deadlineDate).toLocaleDateString('cs-CZ') : '—'}
                </td>
                <td style={tdStyle}>
                  <Badge color={doc.isCompleted ? 'success' : overdue ? 'danger' : 'informative'}>
                    {doc.isCompleted ? 'Splněno' : overdue ? 'Po termínu' : 'Čeká'}
                  </Badge>
                </td>
                <td style={{ ...tdStyle, fontSize: 12 }}>
                  {doc.completedAt ? new Date(doc.completedAt).toLocaleDateString('cs-CZ') : '—'}
                </td>
                <td style={tdStyle}>
                  {!doc.isCompleted && doc.documentType !== 3 && (doc.documentType === 1 ? canSign : true) && (
                    <Button size="small" appearance="primary"
                      disabled={completeMutation.isPending}
                      onClick={() => completeMutation.mutate(doc.id)}>
                      {doc.documentType === 1 ? 'Zaznamenat podpis' : 'Potvrdit seznámení'}
                    </Button>
                  )}
                  {!doc.isCompleted && doc.documentType === 3 && (
                    <span style={{ fontSize: 12, color: 'var(--colorNeutralForeground3, #888)' }}>Výsledek z LMS</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Hlavní stránka ───────────────────────────────────────────────────────
export default function EmployeeDetailPage() {
  const styles = useStyles()
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isHR, isAdmin, isManager } = useAuth()

  const { data, isLoading } = useQuery({
    queryKey: ['employee', id],
    queryFn: () => employeesService.getById(Number(id)),
    enabled: !!id,
  })

  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!data?.entraObjectId || IS_MOCK) return
    let objectUrl: string | null = null
    getEntraUserPhoto(data.entraObjectId).then(url => {
      objectUrl = url
      setPhotoUrl(url)
    })
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [data?.entraObjectId])

  if (isLoading) return <Spinner label={t('app.loading')} />
  if (!data) return <Text>{t('app.error')}</Text>

  const hasAddress = data.address || data.city || data.postalCode
  const bankAccount = data.bankAccount
  const hasChildren = [1, 2, 3, 4, 5].some(n => data[`child${n}Name` as keyof typeof data])

  return (
    <div>
      <div className={styles.header}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate('/employees')}>
          {t('common.back')}
        </Button>
        <Avatar
          size={48}
          name={data.fullName}
          image={photoUrl ? { src: photoUrl } : undefined}
          style={{ flexShrink: 0 }}
        />
        <div>
          <Text size={600} weight="semibold">{data.fullName}</Text>
          {(data.positionName || data.departmentName) && (
            <div style={{ fontSize: 13, color: tokens.colorNeutralForeground3, marginTop: 1 }}>
              {[data.positionName, data.departmentName].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        {(isHR || isAdmin) && (
          <Button appearance="primary" icon={<Edit24Regular />} onClick={() => navigate(`/employees/${id}/edit`)}>
            {t('common.edit')}
          </Button>
        )}
      </div>

      {/* ── Karta: Základní + Zařazení ── */}
      <div className={styles.card}>
        <Text className={styles.sectionTitle}>Základní údaje</Text>

        <div className={styles.infoGrid} style={{ marginTop: 16 }}>
          <InfoField label="Stav">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Badge color={EMPLOYEE_STATUS[data.status]?.color ?? 'subtle'}>
                {EMPLOYEE_STATUS[data.status]?.label ?? String(data.status)}
              </Badge>
              {(data.status === 3 || data.status === 4) && data.statusFrom && (
                <span style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>
                  od {fmt(data.statusFrom)}
                </span>
              )}
            </div>
          </InfoField>

          <InfoField label="Datum nástupu" value={fmt(data.hireDate)} />
          {data.cooperationStartDate && (() => {
            const start = new Date(data.cooperationStartDate)
            const today = new Date()
            let years = today.getFullYear() - start.getFullYear()
            const m = today.getMonth() - start.getMonth()
            if (m < 0 || (m === 0 && today.getDate() < start.getDate())) years--
            return years >= 0 ? (
              <>
                <InfoField label="Zahájení spolupráce" value={fmt(data.cooperationStartDate)} />
                <InfoField label="Délka spolupráce" value={`${years} ${years === 1 ? 'rok' : years <= 4 ? 'roky' : 'let'}`} />
              </>
            ) : null
          })()}

          <InfoField label="Typ pracovního poměru" value={EMPLOYMENT_LABELS[data.employmentType] ?? String(data.employmentType)} />
          <InfoField label="Typ smlouvy" value={CONTRACT_LABELS[data.contractType] ?? String(data.contractType)} />

          {data.contractType === 2 && (
            <InfoField label="Datum ukončení smlouvy" value={fmt(data.contractEndDate)} />
          )}
          <InfoField label="Ukončení zkušební doby" value={fmt(data.probationEndDate)} />
          {data.terminationDate && (
            <InfoField label="Datum ukončení" value={fmt(data.terminationDate)} />
          )}
        </div>

        {/* Pracovní zařazení */}
        <div className={styles.subsection}>Pracovní zařazení</div>
        <div className={styles.infoGrid}>
          <InfoField label="Oddělení"    value={data.departmentName} />
          <InfoField label="Pozice"      value={data.positionName} />
          <InfoField label="Nadřízený"   value={data.managerFullName} />
          <InfoField label="Středisko"   value={data.costCenter} />
          <InfoField label="Osobní číslo" value={data.personalNumber} />
          <InfoField label="Stravenky"   value={data.hasMealVouchers ? 'Ano' : 'Ne'} />
        </div>

        {/* Pracovní kontakt */}
        <div className={styles.subsection}>Pracovní kontakt</div>
        <div className={styles.infoGrid}>
          <InfoField label="E-mail">
            {data.email
              ? <a href={`mailto:${data.email}`} style={{ color: tokens.colorBrandForeground1 }}>{data.email}</a>
              : null}
          </InfoField>
          <InfoField label="Telefon" value={data.phone} />
        </div>
      </div>

      {/* ── Soukromé údaje — pouze HR / Admin ── */}
      {(isHR || isAdmin) && (
        <div className={styles.card} style={{ marginTop: 24 }}>
          <Text className={styles.sectionTitle}>Soukromé údaje</Text>

          <div className={styles.infoGrid} style={{ marginTop: 16 }}>
            <InfoField label="Datum narození"      value={fmt(data.birthDate)} />
            <InfoField label="Rodné číslo"         value={data.personalId} />
            <InfoField label="Zdravotní pojišťovna" value={data.healthInsurance} />
            <InfoField label="Kód pojišťovny"      value={data.healthInsuranceCode} />
            <InfoField label="Osobní e-mail">
              {data.personalEmail
                ? <a href={`mailto:${data.personalEmail}`} style={{ color: tokens.colorBrandForeground1 }}>{data.personalEmail}</a>
                : null}
            </InfoField>
            <InfoField label="Osobní telefon"  value={data.personalPhone} />
          </div>

          {(data.nextOfKinName || data.nextOfKinAddress || data.nextOfKinEmail || data.nextOfKinPhone) && (
            <>
              <div className={styles.subsection}>Osoba blízká</div>
              <div className={styles.infoGrid}>
                <InfoField label="Jméno"    value={data.nextOfKinName} />
                <InfoField label="Adresa"   value={data.nextOfKinAddress} />
                <InfoField label="E-mail"   value={data.nextOfKinEmail} />
                <InfoField label="Telefon"  value={data.nextOfKinPhone} />
              </div>
            </>
          )}

          {hasAddress && (
            <>
              <div className={styles.subsection}>Adresa</div>
              <div className={styles.infoGrid3}>
                <InfoField label="Ulice a číslo" value={data.address} />
                <InfoField label="Obec"           value={data.city} />
                <InfoField label="PSČ"            value={data.postalCode} />
                <InfoField label="Stát"           value={data.country} />
              </div>
            </>
          )}

          {bankAccount && (
            <>
              <div className={styles.subsection}>Bankovní spojení</div>
              <div className={styles.infoGrid}>
                <InfoField label="Číslo účtu" value={bankAccount} />
              </div>
            </>
          )}

          {data.vehicle && (
            <>
              <div className={styles.subsection}>Ostatní</div>
              <div className={styles.infoGrid}>
                <InfoField label="Vozidlo" value={data.vehicle} />
              </div>
            </>
          )}

          {hasChildren && (
            <>
              <div className={styles.subsection}>Děti</div>
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[1, 2, 3, 4, 5].filter(n => data[`child${n}Name` as keyof typeof data]).map(n => {
                  const name       = data[`child${n}Name`       as keyof typeof data] as string
                  const personalId = data[`child${n}PersonalId` as keyof typeof data] as string | undefined
                  const studies    = data[`child${n}Studies`    as keyof typeof data] as boolean | undefined
                  return (
                    <div key={n} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 32px' }}>
                      <div>
                        <div className={styles.infoLabel}>Jméno</div>
                        <div className={styles.infoValue}>{name}</div>
                      </div>
                      <div>
                        {personalId ? (
                          <>
                            <div className={styles.infoLabel}>Rodné číslo</div>
                            <div className={styles.infoValue}>{personalId}</div>
                          </>
                        ) : null}
                      </div>
                      <div>
                        {studies ? (
                          <>
                            <div className={styles.infoLabel}>Studuje</div>
                            <div className={styles.infoValue}>Ano</div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {data.note && (
            <>
              <div className={styles.subsection}>Poznámka</div>
              <div style={{ fontSize: 14, color: tokens.colorNeutralForeground2, fontStyle: 'italic', marginTop: 8, lineHeight: 1.5 }}>
                {data.note}
              </div>
            </>
          )}
        </div>
      )}

      {/* Pracovní rozvrh */}
      {(isHR || isAdmin || isManager) && (
        <WorkScheduleCard
          workScheduleJson={data.workScheduleJson}
          weeklyHours={data.weeklyHours ?? 40}
          styles={styles}
        />
      )}

      {/* Historie úvazku */}
      {(isHR || isAdmin || isManager) && (
        <WorkloadHistoryCard employeeId={Number(id)} styles={styles} />
      )}

      {/* Dovolená */}
      {(isHR || isAdmin || isManager) && (
        <VacationCard
          employeeId={Number(id)}
          employmentType={data.employmentType}
          styles={styles}
          canEdit={isHR || isAdmin}
        />
      )}

      {/* Příspěvek na stáří */}
      {(isHR || isAdmin) && (
        <PensionContributionCard
          employeeId={Number(id)}
          styles={styles}
        />
      )}

      {/* Dokumenty */}
      {(isHR || isAdmin) && (
        <DocumentsCard
          employeeId={Number(id)}
          styles={styles}
          canSign={isHR || isAdmin}
        />
      )}
    </div>
  )
}
